import React, { useState, useMemo } from 'react';
import { auth, functions, storage } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { THEMA_CODES, AUTHOR_ACCENT_COLORS, getAccentClasses } from '../lib/themaCodes';
import {
  BookOpen, User, Palette, ChevronRight, ChevronLeft, Check, Upload,
  Globe, Instagram, ExternalLink, Search, X
} from 'lucide-react';

const STEPS = [
  { id: 'account', label: 'Create Account', icon: User },
  { id: 'branding', label: 'Customise Portal', icon: Palette },
  { id: 'book', label: 'Add First Book', icon: BookOpen },
];

export default function AuthorSignup() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);

  // Step 1: Account
  const [name, setName] = useState('');
  const [penName, setPenName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [genres, setGenres] = useState([]);
  const [genreSearch, setGenreSearch] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Step 2: Branding
  const [accentColor, setAccentColor] = useState('teal');
  const [bio, setBio] = useState('');
  const [website, setWebsite] = useState('');
  const [socialLinks, setSocialLinks] = useState({
    instagram: '', tiktok: '', twitter: '', facebook: ''
  });
  const [displayName, setDisplayName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');

  // Step 3: First Book
  const [bookTitle, setBookTitle] = useState('');
  const [bookSeries, setBookSeries] = useState('');
  const [bookSeriesNumber, setBookSeriesNumber] = useState('');
  const [bookGenres, setBookGenres] = useState([]);
  const [bookGenreSearch, setBookGenreSearch] = useState('');
  const [bookPubDate, setBookPubDate] = useState('');
  const [bookDescription, setBookDescription] = useState('');
  const [bookCoverFile, setBookCoverFile] = useState(null);
  const [bookCoverPreview, setBookCoverPreview] = useState('');

  const filteredGenres = useMemo(() => {
    if (!genreSearch.trim()) return THEMA_CODES.slice(0, 15);
    const q = genreSearch.toLowerCase();
    return THEMA_CODES.filter(t =>
      t.label.toLowerCase().includes(q) || t.code.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [genreSearch]);

  const filteredBookGenres = useMemo(() => {
    if (!bookGenreSearch.trim()) return THEMA_CODES.slice(0, 15);
    const q = bookGenreSearch.toLowerCase();
    return THEMA_CODES.filter(t =>
      t.label.toLowerCase().includes(q) || t.code.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [bookGenreSearch]);

  const accent = getAccentClasses(accentColor);

  function handleFileUpload(file, setFile, setPreview) {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  }

  async function uploadFile(file, path) {
    if (!file) return '';
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  }

  function toggleGenre(code, list, setList) {
    setList(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  }

  const [showLinkOption, setShowLinkOption] = useState(false);

  async function completeAuthorSetup(user) {
    // Call Cloud Function to create author document & set claims (merges roles)
    const createAuthorFn = httpsCallable(functions, 'createAuthor');
    await createAuthorFn({
      name,
      penName,
      email,
      genres: genres.map(code => {
        const match = THEMA_CODES.find(t => t.code === code);
        return { code, label: match?.label || code };
      }),
      website,
      socialLinks,
      bio,
    });

    // Force token refresh to get new claims
    await user.getIdToken(true);

    // Upload files after account is created (non-blocking)
    if (logoFile || bannerFile || bookCoverFile) {
      try {
        if (logoFile) await uploadFile(logoFile, `authors/${user.uid}/logo`);
        if (bannerFile) await uploadFile(bannerFile, `authors/${user.uid}/banner`);
        if (bookCoverFile) await uploadFile(bookCoverFile, `authors/${user.uid}/books/cover-${Date.now()}`);
      } catch {
        // File uploads are optional — don't block signup
      }
    }

    setSignupComplete(true);
  }

  async function handleLinkAccount() {
    setError('');
    setIsSubmitting(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await completeAuthorSetup(user);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Please enter the password for your existing account.');
      } else {
        const msg = err.details || err.message || 'Something went wrong';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCreateAccount() {
    setError('');
    setShowLinkOption(false);
    setIsSubmitting(true);

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }
      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      if (!agreeTerms) {
        throw new Error('You must agree to the terms');
      }

      // Create Firebase Auth account
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Send verification email
      sendEmailVerification(user).catch((err) => {
        console.warn('Failed to send verification email:', err.message);
      });

      await completeAuthorSetup(user);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setShowLinkOption(true);
        setError(
          'This email is already registered. If you have a reader account you can link it to your new author account — enter your existing password below and click "Link Account".'
        );
      } else {
        const msg = err.details || err.message || 'Something went wrong';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function canProceed() {
    if (currentStep === 0) {
      return name.trim() && email.trim() && password.trim() &&
        confirmPassword.trim() && agreeTerms;
    }
    return true; // Steps 2 and 3 are optional
  }

  // After signup completes, redirect to verification holding page
  if (signupComplete) {
    window.location.href = '/author/verify-email';
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100/30">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Narratoria</h1>
            <p className="text-xs text-slate-500">Author Platform</p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <a href="/reader" className="text-slate-500 hover:text-teal-600">
              Also read on Narratoria
            </a>
            <a href="/author/login" className="text-teal-600 hover:text-teal-700">
              Already have an account? Sign in
            </a>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-8 flex items-center justify-center gap-2">
          {STEPS.map((step, i) => {
            const StepIcon = step.icon;
            const isActive = i === currentStep;
            const isDone = i < currentStep;
            return (
              <React.Fragment key={step.id}>
                {i > 0 && (
                  <div className={`h-0.5 w-12 ${isDone ? 'bg-teal-500' : 'bg-slate-200'}`} />
                )}
                <div className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'bg-teal-600 text-white' :
                  isDone ? 'bg-teal-100 text-teal-700' :
                  'bg-slate-100 text-slate-500'
                }`}>
                  {isDone ? <Check className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                  <span className="hidden sm:inline">{step.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {error && (
          <div className={`mb-4 rounded-lg border p-3 text-sm ${showLinkOption ? 'border-teal-200 bg-teal-50 text-teal-800' : 'border-red-200 bg-red-50 text-red-600'}`}>
            <p>{error}</p>
            {showLinkOption && (
              <div className="mt-3 flex items-center gap-2">
                <Button
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={handleLinkAccount}
                  disabled={isSubmitting || !password}
                >
                  {isSubmitting ? 'Linking...' : 'Link Account'}
                </Button>
                <a href="/author/login" className="text-sm text-teal-600 hover:text-teal-700 underline">
                  or sign in instead
                </a>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Account Creation */}
        {currentStep === 0 && (
          <Card className="mx-auto max-w-xl border-slate-200 shadow-md">
            <CardContent className="space-y-4 p-6">
              <h2 className="text-lg font-semibold text-slate-900">Create Your Author Account</h2>
              <p className="text-sm text-slate-600">
                Set up your Narratoria account to start managing your ARC readers.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
                </div>
                <div>
                  <Label>Pen Name</Label>
                  <Input value={penName} onChange={e => setPenName(e.target.value)} placeholder="Optional" />
                </div>
              </div>

              <div>
                <Label>Email *</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Password *</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" />
                </div>
                <div>
                  <Label>Confirm Password *</Label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" />
                </div>
              </div>

              {/* Genre selection */}
              <div>
                <Label>Genres You Write</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    className="pl-9"
                    value={genreSearch}
                    onChange={e => setGenreSearch(e.target.value)}
                    placeholder="Search genres..."
                  />
                </div>
                {genres.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {genres.map(code => {
                      const match = THEMA_CODES.find(t => t.code === code);
                      return (
                        <span key={code} className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-xs text-teal-700">
                          {match?.label || code}
                          <button onClick={() => toggleGenre(code, genres, setGenres)}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="mt-2 max-h-36 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                  {filteredGenres.map(t => (
                    <button
                      key={t.code}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                        genres.includes(t.code) ? 'bg-teal-50 text-teal-700' : 'text-slate-700'
                      }`}
                      onClick={() => toggleGenre(t.code, genres, setGenres)}
                    >
                      <span className="w-10 text-xs text-slate-400">{t.code}</span>
                      <span>{t.label}</span>
                      {genres.includes(t.code) && <Check className="ml-auto h-3 w-3 text-teal-600" />}
                    </button>
                  ))}
                </div>
              </div>

              <label className="relative z-10 flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={e => setAgreeTerms(e.target.checked)}
                  className="h-4 w-4 shrink-0 cursor-pointer rounded border border-slate-300 accent-teal-600"
                />
                <span className="text-sm text-slate-600">
                  I agree to Narratoria's terms of service
                </span>
              </label>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Branding */}
        {currentStep === 1 && (
          <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-2">
            {/* Customisation form */}
            <Card className="border-slate-200 shadow-md">
              <CardContent className="space-y-4 p-6">
                <h2 className="text-lg font-semibold text-slate-900">Customise Your Portal</h2>
                <p className="text-sm text-slate-600">
                  Make your reader portal feel like yours. You can change these later.
                </p>

                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder={`${penName || name}'s ARC Team`}
                  />
                  <p className="mt-1 text-xs text-slate-500">Shown to readers on your portal</p>
                </div>

                <div>
                  <Label>Logo</Label>
                  <div className="mt-1 flex items-center gap-3">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="h-12 w-12 rounded-lg object-cover" />
                    ) : (
                      <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${accent.bg} text-white font-bold text-lg`}>
                        {(penName || name || 'A').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <label className="cursor-pointer rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                      <Upload className="mr-1 inline h-3 w-3" /> Upload
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={e => handleFileUpload(e.target.files[0], setLogoFile, setLogoPreview)}
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <Label>Banner Image</Label>
                  <label className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-4 hover:border-slate-300">
                    {bannerPreview ? (
                      <img src={bannerPreview} alt="Banner" className="h-20 w-full rounded object-cover" />
                    ) : (
                      <>
                        <Upload className="mb-1 h-5 w-5 text-slate-400" />
                        <span className="text-xs text-slate-500">Click to upload banner (recommended 1200x300)</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => handleFileUpload(e.target.files[0], setBannerFile, setBannerPreview)}
                    />
                  </label>
                </div>

                <div>
                  <Label>Accent Colour</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {AUTHOR_ACCENT_COLORS.map(c => (
                      <button
                        key={c.id}
                        onClick={() => setAccentColor(c.id)}
                        className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${
                          accentColor === c.id ? 'border-slate-900 scale-110' : 'border-transparent'
                        }`}
                        title={c.label}
                      >
                        <span className={`h-7 w-7 rounded-full bg-${c.tailwind}-600`} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Author Bio</Label>
                  <Textarea
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    placeholder="Tell readers about yourself..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Website URL</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input className="pl-9" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://yoursite.com" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Instagram</Label>
                    <Input value={socialLinks.instagram} onChange={e => setSocialLinks(p => ({ ...p, instagram: e.target.value }))} placeholder="@handle" />
                  </div>
                  <div>
                    <Label>TikTok</Label>
                    <Input value={socialLinks.tiktok} onChange={e => setSocialLinks(p => ({ ...p, tiktok: e.target.value }))} placeholder="@handle" />
                  </div>
                  <div>
                    <Label>Twitter / X</Label>
                    <Input value={socialLinks.twitter} onChange={e => setSocialLinks(p => ({ ...p, twitter: e.target.value }))} placeholder="@handle" />
                  </div>
                  <div>
                    <Label>Facebook</Label>
                    <Input value={socialLinks.facebook} onChange={e => setSocialLinks(p => ({ ...p, facebook: e.target.value }))} placeholder="Page URL" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Live Preview */}
            <Card className="border-slate-200 shadow-md">
              <CardContent className="p-0">
                <div className="rounded-t-lg bg-slate-800 p-3 text-center text-xs text-slate-400">
                  Preview — Your Reader Portal
                </div>
                {/* Banner */}
                <div className={`h-24 ${bannerPreview ? '' : `bg-gradient-to-r from-slate-700 to-${accentColor === 'teal' ? 'teal' : accentColor}-800`}`}>
                  {bannerPreview && (
                    <img src={bannerPreview} alt="Banner" className="h-full w-full object-cover" />
                  )}
                </div>
                {/* Author info */}
                <div className="relative px-4 pb-4">
                  <div className="absolute -top-8 left-4">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="h-16 w-16 rounded-xl border-4 border-white object-cover shadow-md" />
                    ) : (
                      <div className={`flex h-16 w-16 items-center justify-center rounded-xl border-4 border-white ${accent.bg} text-white font-bold text-2xl shadow-md`}>
                        {(penName || name || 'A').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="ml-20 pt-1">
                    <h3 className="font-semibold text-slate-900">
                      {displayName || `${penName || name || 'Author'}'s ARC Team`}
                    </h3>
                    <p className="text-xs text-slate-500">Powered by Narratoria</p>
                  </div>
                  {bio && (
                    <p className="mt-3 text-sm text-slate-600 line-clamp-3">{bio}</p>
                  )}
                  <div className="mt-3 flex gap-2">
                    {socialLinks.instagram && (
                      <span className="rounded-full bg-slate-100 p-1.5">
                        <Instagram className="h-3 w-3 text-slate-500" />
                      </span>
                    )}
                    {website && (
                      <span className="rounded-full bg-slate-100 p-1.5">
                        <Globe className="h-3 w-3 text-slate-500" />
                      </span>
                    )}
                  </div>
                  {/* Mock nav */}
                  <div className="mt-4 flex gap-3 border-b border-slate-200 pb-2">
                    {['Available ARCs', 'My Books', 'Community'].map((tab, i) => (
                      <span key={tab} className={`text-xs font-medium ${i === 0 ? `${accent.text} border-b-2 border-current pb-1` : 'text-slate-400'}`}>
                        {tab}
                      </span>
                    ))}
                  </div>
                  {/* Mock content */}
                  <div className="mt-3 space-y-2">
                    <div className="h-16 rounded-lg bg-slate-50 border border-slate-100" />
                    <div className="h-16 rounded-lg bg-slate-50 border border-slate-100" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: First Book */}
        {currentStep === 2 && (
          <Card className="mx-auto max-w-xl border-slate-200 shadow-md">
            <CardContent className="space-y-4 p-6">
              <h2 className="text-lg font-semibold text-slate-900">Add Your First Book</h2>
              <p className="text-sm text-slate-600">
                Optional — you can always add books later from your dashboard.
              </p>

              <div>
                <Label>Book Title</Label>
                <Input value={bookTitle} onChange={e => setBookTitle(e.target.value)} placeholder="e.g. Born from the Broken" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Series Name</Label>
                  <Input value={bookSeries} onChange={e => setBookSeries(e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <Label>Book #</Label>
                  <Input type="number" value={bookSeriesNumber} onChange={e => setBookSeriesNumber(e.target.value)} placeholder="1" />
                </div>
              </div>

              <div>
                <Label>Genre Tags</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    className="pl-9"
                    value={bookGenreSearch}
                    onChange={e => setBookGenreSearch(e.target.value)}
                    placeholder="Search THEMA genres..."
                  />
                </div>
                {bookGenres.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {bookGenres.map(code => {
                      const match = THEMA_CODES.find(t => t.code === code);
                      return (
                        <span key={code} className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-xs text-teal-700">
                          {match?.label || code}
                          <button onClick={() => toggleGenre(code, bookGenres, setBookGenres)}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="mt-2 max-h-32 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                  {filteredBookGenres.map(t => (
                    <button
                      key={t.code}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                        bookGenres.includes(t.code) ? 'bg-teal-50 text-teal-700' : 'text-slate-700'
                      }`}
                      onClick={() => toggleGenre(t.code, bookGenres, setBookGenres)}
                    >
                      <span className="w-10 text-xs text-slate-400">{t.code}</span>
                      <span>{t.label}</span>
                      {bookGenres.includes(t.code) && <Check className="ml-auto h-3 w-3 text-teal-600" />}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Cover Image</Label>
                <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 hover:border-slate-300">
                  {bookCoverPreview ? (
                    <img src={bookCoverPreview} alt="Cover" className="h-20 w-14 rounded object-cover" />
                  ) : (
                    <div className="flex h-20 w-14 items-center justify-center rounded bg-slate-200">
                      <BookOpen className="h-5 w-5 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-slate-700">Upload cover</span>
                    <p className="text-xs text-slate-500">JPEG or PNG, recommended 600x900</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => handleFileUpload(e.target.files[0], setBookCoverFile, setBookCoverPreview)}
                  />
                </label>
              </div>

              <div>
                <Label>Target Publication Date</Label>
                <Input type="date" value={bookPubDate} onChange={e => setBookPubDate(e.target.value)} />
              </div>

              <div>
                <Label>Brief Description</Label>
                <Textarea
                  value={bookDescription}
                  onChange={e => setBookDescription(e.target.value)}
                  placeholder="What's your book about? (Shown to ARC applicants)"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="mx-auto mt-6 flex max-w-xl items-center justify-between">
          {currentStep > 0 ? (
            <Button
              variant="outline"
              onClick={() => setCurrentStep(s => s - 1)}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
          ) : (
            <div />
          )}

          {currentStep < STEPS.length - 1 ? (
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setCurrentStep(s => s + 1)}
                  className="text-slate-500"
                >
                  Skip
                </Button>
              )}
              <Button
                onClick={() => {
                  if (currentStep === 0 && !canProceed()) {
                    setError('Please fill in all required fields');
                    return;
                  }
                  setError('');
                  setCurrentStep(s => s + 1);
                }}
                className="gap-1 bg-teal-600 hover:bg-teal-700 text-white"
                disabled={currentStep === 0 && !canProceed()}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={handleCreateAccount}
                className="text-slate-500"
                disabled={isSubmitting}
              >
                Skip & Create Account
              </Button>
              <Button
                onClick={handleCreateAccount}
                className="gap-1 bg-teal-600 hover:bg-teal-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
                <Check className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
